// @ts-nocheck
/* eslint-disable no-param-reassign */
import qs from 'query-string';
import React, { useSyncExternalStore } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/database';
import { getDatabase, ref, off } from 'firebase/database';
import { toast } from 'react-toastify';

import { FIREBASE_CONFIGS } from '../configs';

import { setRoot } from './root';
import useUIStore from './ui';
import useAuthStore from './auth';
import useAdminStore from './admin';
import useAccountStatusStore from './accountStatus';
import useAnalyticsStore from './analytics';
import useRealtimeStore from './realtime';
import useClientHooksStore from './clientHooks';
import useEmailManagementStore from './emailManagement';
import useEmailSuppressionStore from './emailSuppression';
import useFormsStore from './forms';
import useNotificationsStore from './notifications';
import usePaymentsStore from './payments';
import useTourStore from './tour';
import useAMPStore from './amp';

import useOMI366Store from './integrations/OMI366';
import useHubSpotStore from './integrations/HubSpot';
import useSalesforceStore from './integrations/Salesforce';
import useWiXStore from './integrations/WiX';
import useGoogleAdwordsStore from './integrations/GoogleAdwords';
import useGoogleTagManagerStore from './integrations/GoogleTagManager';
import useShopifyStore from './integrations/Shopify';
import useMailchimpStore from './integrations/Mailchimp';
import useSalesforceMarketingCloudStore from './integrations/SalesforceMarketingCloud';
import useTypeformStore from './integrations/Typeform';
import usePipedriveStore from './integrations/Pipedrive';
import useBlitzStore from './integrations/Blitz';
import useFanbridgeStore from './integrations/Fanbridge';

const integrationStores = {
  OMI366: useOMI366Store,
  HubSpot: useHubSpotStore,
  Salesforce: useSalesforceStore,
  WiX: useWiXStore,
  GoogleAdwords: useGoogleAdwordsStore,
  GoogleTagManager: useGoogleTagManagerStore,
  Shopify: useShopifyStore,
  Mailchimp: useMailchimpStore,
  SalesforceMarketingCloud: useSalesforceMarketingCloudStore,
  Typeform: useTypeformStore,
  Pipedrive: usePipedriveStore,
  Blitz: useBlitzStore,
  Fanbridge: useFanbridgeStore,
};

const slices = {
  ui: useUIStore,
  auth: useAuthStore,
  admin: useAdminStore,
  accountStatus: useAccountStatusStore,
  analytics: useAnalyticsStore,
  realtime: useRealtimeStore,
  clientHooks: useClientHooksStore,
  emailManagement: useEmailManagementStore,
  emailSuppression: useEmailSuppressionStore,
  forms: useFormsStore,
  notifications: useNotificationsStore,
  payments: usePaymentsStore,
  tour: useTourStore,
  amp: useAMPStore,
};

// ─── Root setup ─────────────────────────────────────────────────────────────
const firebaseApp = firebase.initializeApp(FIREBASE_CONFIGS);
const initQueryParams = qs.parse(window.location.search);

let appInitFlag = false;

const initAppState = async () => {
  if (appInitFlag) return;
  appInitFlag = true;
  const { user } = useAuthStore.getState();
  useNotificationsStore.getState().attachNotificationsListener();
  if (user.clientId !== 'superadmin' && user.clientId) {
    if (useAuthStore.getState().paidPlan()) {
      useAnalyticsStore.getState().getInfluencedSaves();
    }
    useAnalyticsStore.getState().getRawAnalytics();
    usePaymentsStore.getState().getAvailablePlans();
    useAccountStatusStore.getState().getAccountDetails();
    useFormsStore.getState().getFormLinks();
    useClientHooksStore.getState().getAllHooks();
    useEmailManagementStore.getState().getEmailDomains()
      .then(() => useEmailManagementStore.getState().getTemplates());
  }
};

const resetAppState = () => {
  if (!appInitFlag) return;
  const clientId = useAuthStore.getState().user.clientId;
  if (clientId) off(ref(getDatabase(), `/clientNotifications/${clientId}`));

  useAccountStatusStore.getState().reset();
  useAdminStore.getState().reset();
  useAnalyticsStore.getState().reset();
  useAuthStore.getState().reset();
  useClientHooksStore.getState().reset();
  useFormsStore.getState().reset();
  Object.values(integrationStores).forEach((s) => s.getState().reset());
  useNotificationsStore.getState().reset();
  usePaymentsStore.getState().reset();
  useTourStore.getState().reset();
  useEmailManagementStore.getState().reset();
  useEmailSuppressionStore.getState().reset();
  useAMPStore.getState().reset();
  appInitFlag = false;
};

const root = {
  firebase: firebaseApp,
  initQueryParams,
  initAppState,
  resetAppState,

  get appInitFlag() { return appInitFlag; },

  success: (title = '', id) => toast.success(title, { toastId: `${id} success` }),
  failure: (title = '', id) => toast.error(title, { autoClose: false, toastId: id }),
  clearToast: (id) => toast.dismiss(id),
  clearAllToasts: () => toast.dismiss(),
};

// MobX `@computed` properties translated to zustand stay as 0-arg functions
// in state (e.g. `chartData()`). To keep consumer code reading them as
// plain fields, list them per slice and the root accessor wraps the state
// with `get` properties that auto-invoke them.
const COMPUTED_KEYS = {
  auth: ['paidPlan', 'setupStatus'],
  analytics: [
    'chartData', 'filteredAnalytics', 'totalSaves',
    'totalDirectRecaptures', 'totalInfluencedSaved',
  ],
  payments: ['subtotal', 'promoValue', 'total'],
  emailManagement: ['activeEmailDomain'],
  tour: ['displayTour', 'currentTour'],
  ui: ['mobile'],
};

const withComputed = (state, keys) => {
  if (!keys || keys.length === 0) return state;
  const wrapped = Object.create(state);
  keys.forEach((k) => {
    const fn = state[k];
    if (typeof fn === 'function') {
      Object.defineProperty(wrapped, k, {
        enumerable: true,
        get() { return fn.call(state); },
      });
    }
  });
  return wrapped;
};

// Defines property accessors so root.foo returns the live store state with
// computed values pre-resolved.
Object.entries(slices).forEach(([key, useSlice]) => {
  Object.defineProperty(root, key, {
    get() { return withComputed(useSlice.getState(), COMPUTED_KEYS[key]); },
    enumerable: true,
  });
});

root.integrations = {};
Object.entries(integrationStores).forEach(([key, useSlice]) => {
  Object.defineProperty(root.integrations, key, {
    get() { return useSlice.getState(); },
    enumerable: true,
  });
});

setRoot(root);

// Hook firebase auth listener — replicates Auth constructor behavior.
firebaseApp.auth().onIdTokenChanged((user) => useAuthStore.getState().idTokenCallback(firebaseApp, user));
useAuthStore.getState().attachInterceptor();

// Push Shopify supplemental tour if appropriate.
if (initQueryParams.partner === 'shopify') {
  useTourStore.getState().appendShopify();
}

// Wix auto-login per existing behavior.
if (initQueryParams.partner === 'wix' && initQueryParams.instance) {
  useAuthStore.getState().wixLogin(initQueryParams.instance);
}

// Initial UI width sync.
if (typeof window !== 'undefined') {
  const sync = () => useUIStore.setState({ width: window.innerWidth });
  window.addEventListener('resize', sync);
  sync();
}

// ─── Subscriber utilities ───────────────────────────────────────────────────
// `root` is a stable reference whose properties resolve to live store state
// via getters. For `useSyncExternalStore` to trigger re-renders, the
// snapshot must change on every store update — so we tick a version counter
// inside the subscription wrapper and return that as the snapshot value.
// Components still consume `root` (returned separately by `useStore`).
let globalVersion = 0;

const subscribeAll = (listener) => {
  const onAnyChange = () => {
    globalVersion += 1;
    listener();
  };
  const unsubs = [
    ...Object.values(slices),
    ...Object.values(integrationStores),
  ].map((s) => s.subscribe(onAnyChange));
  return () => unsubs.forEach((u) => u());
};

const getVersion = () => globalVersion;

export const useStore = () => {
  useSyncExternalStore(subscribeAll, getVersion, getVersion);
  return root;
};

/**
 * withStore — drop-in replacement for `@inject('store') @observer`. Passes
 * a `store` prop matching the MobX root shape. Re-renders on any store
 * change (matches MobX-style coarse-grained reactivity).
 */
export const withStore = (Component) => {
  function StoreWrapper(props) {
    const store = useStore();
    return <Component {...props} store={store} />;
  }
  StoreWrapper.displayName = `withStore(${Component.displayName || Component.name || 'Component'})`;
  return StoreWrapper;
};

export default root;

export {
  useUIStore, useAuthStore, useAdminStore, useAccountStatusStore,
  useAnalyticsStore, useRealtimeStore, useClientHooksStore,
  useEmailManagementStore, useEmailSuppressionStore, useFormsStore,
  useNotificationsStore, usePaymentsStore, useTourStore, useAMPStore,
};
